/**
 * Visualizer Mode Registry
 *
 * Maps visualizer mode names to React renderer components. Each mode defines
 * what data types it can render, whether it supports editing, and the component
 * that does the rendering.
 *
 * This replaces the old pattern of registering visualizers as pipeline tools.
 * Visualizers are now panel modifiers — the registry is consulted by the
 * PanelVisualizer component to find the right renderer for the active mode.
 *
 * @see docs/Reaserch/BackendAdvocacyDesignDoc.md §3
 */

import type { ComponentType } from 'react';

import type { ISemanticType } from '@/interfaces/ISemanticType';
import type { VisualizerMode, VisualizerModifiers } from '@/store/useVisualizerStore';

// Re-export manifest helpers under the old names for backward compatibility.
// New code should import directly from '@/lib/visualizerManifest'.
export {
  getDefaultModeForType as getDefaultModeForSemanticType,
  getModeForToolId as getVisualizerModeForTool,
  getModeForToolIdOrDefault as getVisualizerModeForToolOrDefault,
  isVisualizerToolId,
} from '@/lib/visualizerManifest';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Props passed to every visualizer mode renderer component.
 */
export interface VisualizerModeProps {
  /** The data to visualize. */
  data: string;
  /** Detected semantic type of the data (may be null). */
  dataType: ISemanticType | null;
  /** Active display modifiers (syntax highlighting, line numbers, word wrap). */
  modifiers: VisualizerModifiers;
  /** Ephemeral state specific to this mode (tree expand state, search term, etc.). */
  modeState: Record<string, unknown>;
  /** Callback to update mode-specific state. */
  onModeStateChange: (state: Record<string, unknown>) => void;
  /** Whether this panel is in edit mode. */
  editMode: boolean;
  /** Callback for data edits (only called when editMode is true and mode supports editing). */
  onEdit?: (newData: string) => void;
  /** Current app theme. */
  theme: 'light' | 'dark';
}

/**
 * Definition of a single visualizer mode.
 * Adapts existing visualizer tool implementations into the new mode format.
 */
export interface IVisualizerModeDefinition {
  /** Mode identifier (matches VisualizerMode type). */
  id: VisualizerMode;
  /** Display label for the mode selector UI. */
  label: string;
  /** Icon emoji/string for the mode selector. */
  icon: string;
  /** Can this mode render the given data type? */
  canRender: (dataType: ISemanticType | null) => boolean;
  /** React component that renders the visualization. */
  component: ComponentType<VisualizerModeProps>;
  /** Whether this mode supports inline editing. */
  editable: boolean;
  /** Which panes this mode can appear in. Defaults to both. */
  panes?: Array<'input' | 'output'>;
  /** Actions this mode supports in the panel header (e.g., expandAll, collapseAll). */
  headerActions?: string[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Registry
// ─────────────────────────────────────────────────────────────────────────────

class VisualizerModeRegistryImpl {
  private modes = new Map<VisualizerMode, IVisualizerModeDefinition>();

  /** Register a visualizer mode definition. */
  register(definition: IVisualizerModeDefinition): void {
    this.modes.set(definition.id, definition);
  }

  /** Get a mode definition by id. */
  get(id: VisualizerMode): IVisualizerModeDefinition | undefined {
    return this.modes.get(id);
  }

  /** Get all registered mode definitions. */
  getAll(): IVisualizerModeDefinition[] {
    return Array.from(this.modes.values());
  }

  /** Get modes that can render the given data type. */
  getAvailableModes(dataType: ISemanticType | null): IVisualizerModeDefinition[] {
    return this.getAll().filter((mode) => mode.canRender(dataType));
  }

  /** Check if a mode is registered. */
  has(id: VisualizerMode): boolean {
    return this.modes.has(id);
  }

  /** Remove all registered modes (useful for testing). */
  clear(): void {
    this.modes.clear();
  }
}

/** Singleton registry instance. */
export const VisualizerModeRegistry = new VisualizerModeRegistryImpl();

// Tool ID ↔ Mode mapping, semantic type defaults, and isVisualizerToolId
// are all re-exported from visualizerManifest.ts at the top of this file.
// Raw mode is registered via visualizerModeRegistrations.tsx using the
// editor-visualizer tool adapter — no stub needed here.
